import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { asArray } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, DarkPanel, btnOnDark } from '../components/ui';
import { fmtLocal } from '../utils/time';

/** Set when the user leaves for Stripe Checkout; cleared once the plan is active. */
const CHECKOUT_PENDING_KEY = 'sx_checkout_pending';

/** Fallback if API is unreachable — matches admin-published defaults */
const FALLBACK_PLANS = [
  { code: 'MONTHLY', label: 'Monthly', price: 29, originalPrice: 39, months: 1, description: 'Full access, billed every month', perMonth: 29 },
  { code: 'QUARTERLY', label: '3 Months', price: 79, originalPrice: 99, months: 3, description: 'Best for trying the service', perMonth: 26.33 },
  { code: 'SEMI_ANNUAL', label: '6 Months', price: 139, originalPrice: 169, months: 6, description: 'Great mid-term value', perMonth: 23.17 },
  { code: 'YEARLY', label: 'Yearly', price: 229, originalPrice: 299, months: 12, description: 'Best value — save vs monthly', perMonth: 19.08 },
];

function savingsPercent(price, original) {
  if (!original || original <= price) return 0;
  return Math.round(((original - price) / original) * 100);
}

export default function Subscribe() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const subscriptionExpired = location.state?.reason === 'trial_expired'
    || location.state?.reason === 'subscription_expired'
    || (user && user.role === 'ROLE_USER' && !user.subscriptionActive);
  const trialExpired = subscriptionExpired;
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [selected, setSelected] = useState('QUARTERLY');
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get('/payment/plans')
      .then(r => {
        if (r.data?.plans?.length) {
          setPlans(r.data.plans.map(p => ({
            ...p,
            price: Number(p.price),
            originalPrice: Number(p.originalPrice ?? p.price),
            perMonth: Number(p.perMonth ?? p.price / p.months),
          })));
          // Prefer quarterly if present, else first plan
          const codes = r.data.plans.map(p => p.code);
          if (codes.includes('QUARTERLY')) setSelected('QUARTERLY');
          else if (codes.length) setSelected(codes[0]);
        }
        setStripeEnabled(!!r.data?.stripeEnabled);
      })
      .catch(() => {});

    // (Stripe now returns to the public /payment/result page — see PaymentResult.jsx.)
    api.get('/payment/my').then(r => setPayments(asArray(r.data))).catch(() => {});
  }, []);

  // Paid in the phone's browser and came back to the app: AuthContext re-checks the
  // subscription when the app returns to the foreground — once it's active, go on in.
  useEffect(() => {
    let pending = false;
    try { pending = sessionStorage.getItem(CHECKOUT_PENDING_KEY) === '1'; } catch (_) {}
    if (pending && user?.subscriptionActive && !user?.needsPayment) {
      try { sessionStorage.removeItem(CHECKOUT_PENDING_KEY); } catch (_) {}
      navigate('/signals');
    }
  }, [user?.subscriptionActive, user?.needsPayment]);

  const selectedPlan = plans.find(p => p.code === selected) || plans[0];

  const handlePay = async () => {
    setMsg('');
    setError('');
    setLoading(true);
    try {
      const res = await api.post(`/payment/checkout?plan=${selected}`);
      if (res.data.mode === 'STRIPE' && res.data.checkoutUrl) {
        try { sessionStorage.setItem(CHECKOUT_PENDING_KEY, '1'); } catch (_) {}
        // Web: goes to Stripe in this tab. Android app: Capacitor opens external URLs
        // in the phone's browser; the plan is activated by the Stripe webhook.
        window.location.href = res.data.checkoutUrl;
        return;
      }
      if (res.data.mode !== 'DEMO') {
        setError(res.data.message || 'Payments are not available right now.');
        return;
      }
      // Local testing only — the server refuses demo payments in production.
      const demo = await api.post('/payment/demo-confirm', { plan: selected });
      setMsg((demo.data.message || 'Plan activated') + (demo.data.reference ? ` · Ref ${demo.data.reference}` : ''));
      const u = await refreshUser();
      const p = await api.get('/payment/my');
      setPayments(asArray(p.data));
      if (u?.subscriptionActive) navigate('/signals');
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-8">
      <PageHeader
        title={trialExpired ? 'Activate your account' : 'Choose your plan'}
        subtitle={subscriptionExpired
          ? 'Your access has ended. Choose a plan below to activate or renew your subscription.'
          : '7-day free trial on signup · Cancel anytime by letting the period end'}
        back={!trialExpired && !subscriptionExpired}
      />

      {trialExpired && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Subscription required.</strong> Your free trial or paid period has ended.
          Activate a plan to continue using signals, movers, earnings, news, and live quotes.
        </div>
      )}

      {user?.subscriptionActive && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex flex-wrap items-center justify-between gap-2">
          <span>
            You’re on an <strong>active</strong> plan
            {user.subscriptionEndDate && (
              <> until <strong>{new Date(user.subscriptionEndDate).toLocaleDateString()}</strong></>
            )}
          </span>
          <Link to="/signals" className="text-emerald-700 font-medium underline-offset-2 hover:underline">
            View signals →
          </Link>
        </div>
      )}

      {msg && (
        <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 text-sm">
          {msg}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Pricing grid — 4 tiers with struck-out originals */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {plans.map((p) => {
          const active = selected === p.code;
          const popular = p.code === 'QUARTERLY' || p.code === 'YEARLY';
          const savePct = savingsPercent(Number(p.price), Number(p.originalPrice));
          const showStrike = Number(p.originalPrice) > Number(p.price);
          return (
            <button
              key={p.code}
              type="button"
              onClick={() => setSelected(p.code)}
              className={`relative text-left rounded-2xl border-2 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-5 text-white shadow-md transition focus:outline-none ${
                active
                  ? 'border-emerald-400 shadow-xl scale-[1.02]'
                  : 'border-slate-700/60 hover:border-slate-500'
              }`}
            >
              {popular && (
                <span className={`absolute -top-2.5 left-4 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  active ? 'bg-emerald-400 text-slate-900' : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {p.code === 'YEARLY' ? 'Best value' : 'Popular'}
                </span>
              )}
              {savePct > 0 && (
                <span className={`absolute -top-2.5 right-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  active ? 'bg-amber-300 text-slate-900' : 'bg-amber-100 text-amber-800'
                }`}>
                  Save {savePct}%
                </span>
              )}
              <p className={`text-sm font-medium ${active ? 'text-slate-300' : 'text-slate-500'}`}>{p.label}</p>
              <p className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                <span className="text-3xl font-bold tabular-nums">${Number(p.price).toFixed(0)}</span>
                <span className={`text-sm ${active ? 'text-slate-400' : 'text-slate-400'}`}>
                  / {p.months === 1 ? 'mo' : `${p.months} mo`}
                </span>
              </p>
              {showStrike && (
                <p className={`mt-0.5 text-sm ${active ? 'text-slate-400' : 'text-slate-400'}`}>
                  <span className="line-through tabular-nums">${Number(p.originalPrice).toFixed(0)}</span>
                  <span className={`ml-1.5 text-[11px] font-semibold uppercase tracking-wide ${
                    active ? 'text-emerald-300' : 'text-emerald-600'
                  }`}>
                    Great value
                  </span>
                </p>
              )}
              <p className={`mt-1 text-xs ${active ? 'text-slate-400' : 'text-slate-500'}`}>
                ~${Number(p.perMonth).toFixed(0)}/mo
              </p>
              <p className={`mt-3 text-xs leading-relaxed ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                {p.description}
              </p>
            </button>
          );
        })}
      </div>

      <DarkPanel className="mb-6" bodyClassName="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-sm text-slate-400">Selected</p>
            <p className="text-lg font-semibold text-white">
              {selectedPlan?.label} — ${Number(selectedPlan?.price || 0).toFixed(0)}
              {selectedPlan?.originalPrice > selectedPlan?.price && (
                <span className="ml-2 text-sm font-normal text-slate-400 line-through">
                  ${Number(selectedPlan.originalPrice).toFixed(0)}
                </span>
              )}
            </p>
          </div>
          <div className="text-right text-xs text-slate-400">
            {stripeEnabled ? (
              <span className="inline-flex items-center gap-1 text-emerald-300 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Secure checkout
              </span>
            ) : (
              <span className="text-slate-400">Activate plan</span>
            )}
          </div>
        </div>

        <button onClick={handlePay} disabled={loading} className={`${btnOnDark} w-full py-3 text-base`}>
          {loading
            ? 'Please wait…'
            : `Activate ${selectedPlan?.label} · $${Number(selectedPlan?.price || 0).toFixed(0)}`}
        </button>
      </DarkPanel>

      {payments.length > 0 && (
        <DarkPanel title="Payment history" bodyClassName="space-y-1.5 p-2.5">
            {payments.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3 text-sm"
              >
                <div>
                  <span className="font-semibold tabular-nums text-white">${Number(p.amount).toFixed(2)}</span>
                  <span className="text-slate-500 mx-2">·</span>
                  <span className="text-slate-300">{p.description || p.provider}</span>
                  <span className={`ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded ${
                    p.status === 'PAID' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-white/10 text-slate-400'
                  }`}>{p.status}</span>
                </div>
                <span className="text-xs text-slate-500">
                  {fmtLocal(p.paidAt || p.createdAt)}
                </span>
              </div>
            ))}
        </DarkPanel>
      )}
    </div>
  );
}
